/**
 * src/pages/SLA.jsx
 * Panel de SLA: semáforo de sistema por cliente (frescura de datos, errores,
 * próximos runs). Capa 3 de observabilidad — ver SLA_CM_MONITOR.md.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { KpiCard, Card } from '../components/ui/Card'
import { Badge, RESULTADO_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { useSlaResumen, useSlaInforme } from '../hooks/useSla'
import { fechaHoraCL } from '../lib/horaChile'

const SEMAFORO = {
  verde:    { color: 'green',  label: 'OK' },
  amarillo: { color: 'yellow', label: 'ATENCIÓN' },
  rojo:     { color: 'red',    label: 'CRÍTICO' },
  gris:     { color: 'gray',   label: 'INACTIVO' },
}

function fmtHoras(h) {
  if (h == null) return '—'
  if (h < 1) return `hace ${Math.round(h * 60)} min`
  if (h < 48) return `hace ${Math.round(h)} h`
  return `hace ${Math.round(h / 24)} días`
}

function fmtFecha(iso) {
  return fechaHoraCL(iso, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }) || '—'
}

const COLUMNS = (navigate) => [
  {
    key: 'semaforo', label: 'Estado', width: '110px',
    render: (v, row) => {
      const s = SEMAFORO[v] || SEMAFORO.gris
      return (
        <span title={row.motivo}>
          <Badge color={s.color} dot pulse={row.corriendo}>
            {row.corriendo ? 'CORRIENDO' : s.label}
          </Badge>
        </span>
      )
    },
  },
  {
    key: 'nombre', label: 'Cliente',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.cliente_id}
        </div>
      </div>
    ),
  },
  {
    key: 'horas_desde_ok', label: 'Datos frescos', mono: true, nowrap: true,
    render: (v) => fmtHoras(v),
  },
  {
    key: 'ultimo_resultado_precios', label: 'Último run', width: '100px',
    render: (v) => v
      ? <Badge color={RESULTADO_COLOR[v] || 'gray'}>{v}</Badge>
      : <span style={{ color: 'var(--color-text-3)' }}>—</span>,
  },
  {
    key: 'errores_7d', label: 'Errores 7d', align: 'center', mono: true, width: '90px',
    render: (v) => (
      <span style={{ color: v > 0 ? 'var(--color-error)' : 'var(--color-text-3)', fontWeight: v > 0 ? 600 : 400 }}>
        {v}
      </span>
    ),
  },
  { key: 'freq_precios', label: 'Frecuencia', mono: true, nowrap: true, width: '100px' },
  {
    key: 'proximo_run_precios', label: 'Próximo run', mono: true, nowrap: true,
    render: (v) => fmtFecha(v),
  },
]

// ── Informe mensual (Capa 6) ──────────────────────────────────────────────────

function mesActual() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function mesVecino(mes, delta) {
  const [y, m] = mes.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function fmtMes(mes) {
  const [y, m] = mes.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })
}

const INFORME_COLUMNS = [
  {
    key: 'nombre', label: 'Cliente',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
          {row.cliente_id}
        </div>
      </div>
    ),
  },
  {
    key: 'pct_frescura', label: 'Datos frescos', mono: true, nowrap: true,
    render: (v, row) => (
      <span>
        {row.dias_con_datos}/{row.dias_evaluables} días
        <span style={{ color: 'var(--color-text-3)' }}> · {v}%</span>
      </span>
    ),
  },
  {
    key: 'cumple_sla', label: 'SLA', width: '110px',
    render: (v) => (
      <Badge color={v ? 'green' : 'red'} dot>{v ? 'CUMPLE' : 'INCUMPLE'}</Badge>
    ),
  },
  {
    key: 'pct_exito_runs', label: 'Runs OK', mono: true, nowrap: true,
    render: (v, row) => {
      const total = row.runs_ok + row.runs_error
      if (!total) return <span style={{ color: 'var(--color-text-3)' }}>sin runs</span>
      return <span>{row.runs_ok}/{total} <span style={{ color: 'var(--color-text-3)' }}>· {v}%</span></span>
    },
  },
  {
    key: 'incidencias', label: 'Incidencias', align: 'center', mono: true, width: '100px',
    render: (v) => (
      <span style={{ color: v > 0 ? 'var(--color-warn)' : 'var(--color-text-3)', fontWeight: v > 0 ? 600 : 400 }}>
        {v}
      </span>
    ),
  },
]

function BotonMes({ children, onClick, disabled }) {
  return (
    <button
      onClick={onClick} disabled={disabled}
      style={{
        border: '1px solid var(--color-border)', background: 'var(--color-surface)',
        borderRadius: 'var(--radius-sm)', padding: '3px 9px', cursor: disabled ? 'default' : 'pointer',
        color: disabled ? 'var(--color-text-3)' : 'var(--color-text-2)',
        fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)',
      }}
    >
      {children}
    </button>
  )
}

function descargarCsvInforme(mes, clientes) {
  const cab = ['cliente_id', 'nombre', 'tier', 'dias_con_datos', 'dias_evaluables',
               'pct_frescura', 'cumple_sla', 'runs_ok', 'runs_error', 'pct_exito_runs',
               'incidencias']
  const filas = clientes.map(c => cab.map(k => {
    const v = c[k]
    if (typeof v === 'boolean') return v ? 'SI' : 'NO'
    if (v == null) return ''
    return String(v).includes(';') ? `"${v}"` : v
  }).join(';'))
  // BOM para que Excel abra el UTF-8 con acentos correctos; separador ';' (Excel es-ES/es-CL)
  const csv = '﻿' + [cab.join(';'), ...filas].join('\r\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `informe_sla_${mes}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function InformeMensual() {
  const navigate = useNavigate()
  const [mes, setMes] = useState(mesActual())
  const { data, isLoading } = useSlaInforme(mes)

  return (
    <Card
      title="Informe mensual"
      subtitle="Clic en un cliente para su informe individual (PDF)"
      padding="0"
      style={{ marginTop: 'var(--space-5)' }}
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <BotonMes onClick={() => setMes(mesVecino(mes, -1))}>←</BotonMes>
          <span style={{
            fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
            minWidth: '120px', textAlign: 'center', textTransform: 'capitalize',
          }}>
            {fmtMes(mes)}
          </span>
          <BotonMes onClick={() => setMes(mesVecino(mes, 1))} disabled={mes >= mesActual()}>→</BotonMes>
          <span style={{ width: '8px' }} />
          <BotonMes onClick={() => descargarCsvInforme(mes, data?.clientes || [])}
                    disabled={isLoading || !data?.clientes?.length}>
            ⬇ CSV
          </BotonMes>
          <BotonMes onClick={() => navigate(`/sla/informe/print?mes=${mes}`)}
                    disabled={isLoading}>
            ⬇ PDF
          </BotonMes>
        </div>
      }
    >
      <Table
        columns={INFORME_COLUMNS}
        data={data?.clientes}
        loading={isLoading}
        emptyMessage="Sin datos para este mes"
        onRowClick={row => navigate(`/sla/informe/print?mes=${mes}&cliente=${row.cliente_id}`)}
      />
    </Card>
  )
}

export default function SLA() {
  const navigate = useNavigate()
  const { data, isLoading } = useSlaResumen()

  // ⚠️ Esto era `data?.resumen || {}`. El `|| {}` es la raíz del cero falso que
  // había debajo: convierte «no hay respuesta» en «respuesta con todo a cero»,
  // y a partir de ahí cada campo sale `undefined` y cada `?? 0` lo cierra en 0.
  // Aquí eso significaba que **un fallo de lectura del SLA se veía igual que una
  // flota impecable**: 0 en atención y 0 en crítico. Sin el `|| {}`, los campos
  // llegan ausentes y <KpiCard> los pinta «—».
  const resumen = data?.resumen
  const umbrales = data?.umbrales

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="SLA"
        // ⚠️ Era `umbrales.fresco_h ?? 26`: guardaba el objeto arriba y leía el
        // campo aquí, así que al quitar el `|| {}` la página dejó de montar
        // ENTERA. Cuarta vez que cometo esta forma en el proyecto — y la
        // primera que la caza una prueba en vez de una captura.
        subtitle={umbrales?.fresco_h != null
          ? `Estado de servicio por cliente · umbral de frescura ${umbrales.fresco_h} h`
          : 'Estado de servicio por cliente'}
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        {/* Agregados */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px',
          marginBottom: 'var(--space-5)',
        }}>
          <KpiCard
            label="EN SLA"
            valor={resumen?.verde}
            cargando={isLoading}
            color="var(--color-ok)"
            sub={resumen?.total != null ? `de ${resumen.total} clientes activos` : undefined}
          />
          <KpiCard
            label="ATENCIÓN"
            valor={resumen?.amarillo}
            cargando={isLoading}
            color={resumen?.amarillo > 0 ? 'var(--color-warn)' : 'var(--color-text-3)'}
            sub="frescura al límite o run fallido"
            nada="ningún cliente al límite"
          />
          <KpiCard
            label="CRÍTICO"
            valor={resumen?.rojo}
            cargando={isLoading}
            color={resumen?.rojo > 0 ? 'var(--color-error)' : 'var(--color-text-3)'}
            sub={umbrales?.critico_h != null
              ? `sin datos frescos ≥ ${umbrales.critico_h} h`
              : 'sin datos frescos según el umbral configurado'}
            nada="ningún cliente en crítico"
          />
          <KpiCard
            label="INACTIVOS"
            valor={resumen?.gris}
            cargando={isLoading}
            color="var(--color-text-3)"
            sub="scheduler en pausa"
          />
        </div>

        <Card
          title="Clientes"
          subtitle="Clic en un cliente para ver sus runs y logs"
          padding="0"
        >
          <Table
            columns={COLUMNS(navigate)}
            data={data?.clientes}
            loading={isLoading}
            emptyMessage="Sin clientes activos"
            onRowClick={(row) => navigate(`/ejecuciones/${row.cliente_id}`)}
          />
        </Card>

        <InformeMensual />

        {data?.generado_en && (
          <div style={{
            marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-3)',
            fontFamily: 'var(--font-mono)',
          }}>
            Actualizado {fmtFecha(data.generado_en)} UTC · se refresca cada 60 s
          </div>
        )}
      </div>
    </div>
  )
}
